import { useEffect } from 'react'
import { type Client, useClient } from 'urql'
import { GetAssignment } from '../graphql/assignments'
import { GetOrderDetail } from '../graphql/orders'
import {
  GetCustomerContacts,
  GetOrderDataForSignature,
  GetWorkTicketDataForSignature,
} from '../graphql/signature'
import {
  GetOrderWorkTicketData,
  GetWorkTicketInventory,
} from '../graphql/workTicket'
import { NETWORK_ONLY } from '../helpers/requestPolicy'

/** Minimal assignment subset from GetOperatorWithAssignments. */
type PrefetchAssignment = {
  id: number
  route?: {
    waypoints?: ({ order?: { id?: number | null } | null } | null)[] | null
  } | null
}

/**
 * Run a query and resolve when it (and its cache write) is done. Swallows errors
 * so one failed prefetch doesn't abort the rest.
 */
// biome-ignore lint/suspicious/noExplicitAny: prefetch documents are heterogeneous
const run = (client: Client, doc: any, vars: any) =>
  client
    .query(doc, vars, NETWORK_ONLY)
    .toPromise()
    // biome-ignore lint/suspicious/noExplicitAny: loose result
    .catch((): any => null)

/**
 * Warm the persisted graphcache with every screen reachable from an order —
 * order detail (+ its services), work-ticket data, and the signature/contacts
 * data — so the full offline flow works after losing connection. Orders are
 * processed one at a time and each order's queries are awaited before moving on,
 * so the cache fills completely (and in order) rather than racing.
 */
export const prefetchOrderData = async (
  client: Client,
  orderIds: Iterable<number>,
  shouldStop?: () => boolean,
) => {
  for (const orderId of orderIds) {
    if (shouldStop?.()) return
    const orderRes = await run(client, GetOrderDataForSignature, { orderId })
    await Promise.all([
      run(client, GetOrderDetail, { orderId }),
      run(client, GetOrderWorkTicketData, { orderId }),
      run(client, GetWorkTicketDataForSignature, { orderId }),
    ])
    // biome-ignore lint/suspicious/noExplicitAny: prefetch result is loosely typed
    const customerId = (orderRes?.data as any)?.order?.customer?.id
    if (customerId && !shouldStop?.()) {
      await run(client, GetCustomerContacts, { customerId })
    }
  }
}

/**
 * While online, prefetch a specific set of orders (used from the assignment
 * detail so opening an assignment caches its whole work-ticket/signature flow
 * before the connection drops).
 */
export const usePrefetchOrderData = (orderIds: number[], isOnline: boolean) => {
  const client = useClient()
  // biome-ignore lint/correctness/useExhaustiveDependencies: orderIds joined below
  useEffect(() => {
    if (!isOnline || !orderIds.length) return
    let cancelled = false
    prefetchOrderData(client, orderIds, () => cancelled)
    return () => {
      cancelled = true
    }
  }, [orderIds.join(','), isOnline, client])
}

/**
 * While online, warm the cache with every assignment's screens — assignment
 * detail, order detail (+ services), work-ticket + signature data — so a crew
 * member who later goes offline can still open them and run the full flow, which
 * graphcache queues and replays on reconnect. Assignments are processed in
 * sequence (each fully cached before the next) and it re-runs on reconnect.
 */
export const usePrefetchAssignmentData = (
  assignments: PrefetchAssignment[] | undefined,
  isOnline: boolean,
) => {
  const client = useClient()

  useEffect(() => {
    if (!isOnline || !assignments?.length) return
    let cancelled = false
    prefetchAllAssignments(client, assignments, () => cancelled)
    return () => {
      cancelled = true
    }
  }, [assignments, isOnline, client])
}

/** Inventory first (backs the work-ticket form), then each assignment. */
const prefetchAllAssignments = async (
  client: Client,
  assignments: PrefetchAssignment[],
  shouldStop: () => boolean,
) => {
  await run(client, GetWorkTicketInventory, {})
  for (const a of assignments) {
    if (shouldStop()) return
    await run(client, GetAssignment, { assignmentId: a.id })
    const orderIds = (a.route?.waypoints ?? []).flatMap((wp) =>
      wp?.order?.id ? [wp.order.id] : [],
    )
    await prefetchOrderData(client, orderIds, shouldStop)
  }
}
